import { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import {
  AMADEUS_SINGLE,
  AMADEUS_SEATMAP,
  AMADEUS_AVAILABILITY,
  WEBSOCKET_RECONNECT_MAX,
} from '@/config/timeouts';

/**
 * WebSocket connection status
 */
export type WebSocketStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

/**
 * Progress update from the queue
 */
export interface QueueProgress {
  type: 'queue_status' | 'processing' | 'completed' | 'failed' | 'timeout' | 'error';
  request_id?: string;
  message_id?: string;
  status?: string;
  queue_position?: number;
  request_type?: string;
  page?: number;
  message?: string;
  result?: unknown;
  error?: string;
}

/**
 * Single command result
 */
export interface SingleCommandResult {
  command: string;
  response: string;
}

/**
 * Seatmap result
 */
export interface SeatmapResult {
  command: string;
  raw_response: string;
  pages_fetched: number;
  airline?: string;
  flight_number?: string;
  aircraft?: string;
  cabin_sections?: unknown[];
  legend?: Record<string, string>;
  parse_error?: string;
}

/**
 * Availability result
 */
export interface AvailabilityResult {
  command: string;
  raw_response: string;
  origin?: string;
  destination?: string;
  date?: string;
  day_of_week?: string;
  flights?: unknown[];
  has_more?: boolean;
  parse_error?: string;
}

/**
 * Pending request tracking
 */
interface PendingRequest<T> {
  resolve: (value: T) => void;
  reject: (reason?: unknown) => void;
  onProgress?: (progress: QueueProgress) => void;
}

/**
 * Seatmap search parameters
 */
export interface SeatmapParams {
  airline: string;
  flight_number: string;
  booking_class: string;
  date: string;
  origin: string;
  destination: string;
}

/**
 * Availability search parameters
 */
export interface AvailabilityParams {
  origin: string;
  destination: string;
  date?: string;
  airline?: string;
}

/**
 * Context value interface - what consumers get access to
 */
interface AmadeusWebSocketContextValue {
  status: WebSocketStatus;
  sendCommand: (
    command: string,
    onProgress?: (progress: QueueProgress) => void
  ) => Promise<SingleCommandResult>;
  sendSeatmapRequest: (
    params: SeatmapParams,
    onProgress?: (progress: QueueProgress) => void
  ) => Promise<SeatmapResult>;
  sendAvailabilityRequest: (
    params: AvailabilityParams,
    onProgress?: (progress: QueueProgress) => void
  ) => Promise<AvailabilityResult>;
}

const AmadeusWebSocketContext = createContext<AmadeusWebSocketContextValue | null>(null);

function generateMessageId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

interface AmadeusWebSocketProviderProps {
  children: ReactNode;
}

/**
 * Provider component that manages a shared WebSocket connection for Amadeus commands.
 *
 * Wraps the lawyer dashboard so all child routes share a single WebSocket connection
 * instead of each creating their own. This prevents "WebSocket not connected" errors
 * when navigating between pages.
 */
export function AmadeusWebSocketProvider({ children }: AmadeusWebSocketProviderProps) {
  const [status, setStatus] = useState<WebSocketStatus>('disconnected');
  const wsRef = useRef<WebSocket | null>(null);
  const pendingRequestsRef = useRef<Map<string, PendingRequest<unknown>>>(new Map());
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    setStatus('connecting');

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = import.meta.env.VITE_WS_HOST || window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/ws/chat/`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus('connected');
      reconnectAttemptsRef.current = 0;
    };

    ws.onclose = () => {
      setStatus('disconnected');
      wsRef.current = null;

      // Reject all pending requests
      pendingRequestsRef.current.forEach((request) => {
        request.reject(new Error('WebSocket connection closed'));
      });
      pendingRequestsRef.current.clear();

      // Auto-reconnect with exponential backoff
      const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), WEBSOCKET_RECONNECT_MAX);
      reconnectAttemptsRef.current++;

      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, delay);
    };

    ws.onerror = () => {
      setStatus('error');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as QueueProgress;
        const messageId = data.message_id;

        if (!messageId) return;

        const pendingRequest = pendingRequestsRef.current.get(messageId);
        if (!pendingRequest) return;

        // Handle different message types
        switch (data.type) {
          case 'queue_status':
          case 'processing':
            // Progress updates - call onProgress callback
            pendingRequest.onProgress?.(data);
            break;

          case 'completed':
            // Request completed successfully
            pendingRequestsRef.current.delete(messageId);
            pendingRequest.resolve(data.result);
            break;

          case 'failed':
          case 'timeout':
          case 'error':
            // Request failed
            pendingRequestsRef.current.delete(messageId);
            pendingRequest.reject(new Error(data.error || 'Request failed'));
            break;
        }
      } catch (e) {
        console.error('Error parsing WebSocket message:', e);
      }
    };
  }, []);

  // Connect on mount, cleanup on unmount
  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  /**
   * Send a single Amadeus command through the queue.
   */
  const sendCommand = useCallback(
    async (
      command: string,
      onProgress?: (progress: QueueProgress) => void
    ): Promise<SingleCommandResult> => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        throw new Error('WebSocket not connected');
      }

      const messageId = generateMessageId();

      return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          pendingRequestsRef.current.delete(messageId);
          reject(new Error(`Command timed out after ${AMADEUS_SINGLE / 1000} seconds`));
        }, AMADEUS_SINGLE);

        pendingRequestsRef.current.set(messageId, {
          resolve: (value) => {
            clearTimeout(timeoutId);
            resolve(value as SingleCommandResult);
          },
          reject: (reason) => {
            clearTimeout(timeoutId);
            reject(reason);
          },
          onProgress,
        });

        wsRef.current!.send(
          JSON.stringify({
            type: 'amadeus_queue',
            request_type: 'single',
            command: command,
            message_id: messageId,
          })
        );
      });
    },
    []
  );

  /**
   * Send a seatmap request through the queue.
   * Handles MD pagination atomically.
   */
  const sendSeatmapRequest = useCallback(
    async (
      params: SeatmapParams,
      onProgress?: (progress: QueueProgress) => void
    ): Promise<SeatmapResult> => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        throw new Error('WebSocket not connected');
      }

      const messageId = generateMessageId();

      return new Promise((resolve, reject) => {
        // Longer timeout for seatmap requests (pagination can take time)
        const timeoutId = setTimeout(() => {
          pendingRequestsRef.current.delete(messageId);
          reject(new Error(`Seatmap request timed out after ${AMADEUS_SEATMAP / 1000} seconds`));
        }, AMADEUS_SEATMAP);

        pendingRequestsRef.current.set(messageId, {
          resolve: (value) => {
            clearTimeout(timeoutId);
            resolve(value as SeatmapResult);
          },
          reject: (reason) => {
            clearTimeout(timeoutId);
            reject(reason);
          },
          onProgress,
        });

        wsRef.current!.send(
          JSON.stringify({
            type: 'amadeus_queue',
            request_type: 'seatmap',
            params: params,
            message_id: messageId,
          })
        );
      });
    },
    []
  );

  /**
   * Send an availability search request through the queue.
   */
  const sendAvailabilityRequest = useCallback(
    async (
      params: AvailabilityParams,
      onProgress?: (progress: QueueProgress) => void
    ): Promise<AvailabilityResult> => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        throw new Error('WebSocket not connected');
      }

      const messageId = generateMessageId();

      return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          pendingRequestsRef.current.delete(messageId);
          reject(new Error(`Availability request timed out after ${AMADEUS_AVAILABILITY / 1000} seconds`));
        }, AMADEUS_AVAILABILITY);

        pendingRequestsRef.current.set(messageId, {
          resolve: (value) => {
            clearTimeout(timeoutId);
            resolve(value as AvailabilityResult);
          },
          reject: (reason) => {
            clearTimeout(timeoutId);
            reject(reason);
          },
          onProgress,
        });

        wsRef.current!.send(
          JSON.stringify({
            type: 'amadeus_queue',
            request_type: 'availability',
            params: params,
            message_id: messageId,
          })
        );
      });
    },
    []
  );

  const contextValue: AmadeusWebSocketContextValue = {
    status,
    sendCommand,
    sendSeatmapRequest,
    sendAvailabilityRequest,
  };

  return (
    <AmadeusWebSocketContext.Provider value={contextValue}>
      {children}
    </AmadeusWebSocketContext.Provider>
  );
}

/**
 * Hook to access the shared Amadeus WebSocket connection.
 *
 * Must be used within AmadeusWebSocketProvider (typically within lawyer dashboard).
 * Provides same interface as useAmadeusQueue but uses a shared connection.
 */
export function useAmadeusWebSocket(): AmadeusWebSocketContextValue {
  const context = useContext(AmadeusWebSocketContext);
  if (!context) {
    throw new Error('useAmadeusWebSocket must be used within AmadeusWebSocketProvider');
  }
  return context;
}

export { AmadeusWebSocketContext };
